<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core';
import { Hourglass, RefreshCw } from 'lucide-vue-next';

const store = useOfferExpirationStore();
const criteriaStore = useSearchCriteriaStore();
const localePath = useLocalePath();

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMounted = useMounted();
// SSR + first client render resolve to false (matches server, which has no viewport), then the real
// breakpoint applies after mount — avoids a sheet/dialog hydration mismatch on desktop.
const isDesktop = computed(() => isMounted.value && breakpoints.greaterOrEqual('lg').value);

const prefix = 'components.results.offerExpiration';

const onOpenChange = (value: boolean) => {
  if (!value && store.phase === 'warning') {
    store.dismiss();
  }
};

const onNewSearch = async () => {
  store.$reset();
  await navigateTo(localePath('index'));
};

const onRefresh = async () => {
  const funnel = store.regime === 'funnel';
  store.dismiss();
  if (!criteriaStore.hasSearched) {
    await navigateTo(localePath('index'));
    return;
  }
  // Dumb dialog: only signal the intent. list.vue (the query owner) re-runs the committed search.
  // Funnel: go back to results first so that owner is mounted to pick up the flag.
  criteriaStore.refreshRequested = true;
  if (funnel) {
    await navigateTo(localePath('results'));
  }
};
</script>

<template>
  <UiCustomInfoDialog
    :open="store.open"
    :variant="isDesktop ? 'dialog' : 'sheet'"
    :title="
      store.phase === 'expired' ? $t(`${prefix}.expired.title`) : $t(`${prefix}.warning.title`, store.guaranteedMinutes)
    "
    :icon="store.phase === 'expired' ? RefreshCw : Hourglass"
    hide-close
    @update:open="onOpenChange"
  >
    <template v-if="store.phase !== 'expired'">
      <div class="text-sm leading-6 text-text-secondary-600">
        <p>{{ $t(`${prefix}.warning.${store.regime}.description1`, store.guaranteedMinutes) }}</p>
        <p>{{ $t(`${prefix}.warning.${store.regime}.description2`, store.guaranteedMinutes) }}</p>
      </div>
    </template>

    <template v-else>
      <p class="text-sm leading-6 text-text-secondary-600">
        {{ $t(`${prefix}.expired.description`) }}
      </p>
    </template>

    <template #footer>
      <div
        v-if="store.phase !== 'expired'"
        class="flex w-full justify-center lg:justify-end"
      >
        <UiButton
          class="w-full lg:w-auto"
          @click="store.dismiss()"
        >
          {{ $t(`${prefix}.warning.continue`) }}
        </UiButton>
      </div>

      <div
        v-else
        class="flex w-full flex-col gap-2 lg:flex-row lg:justify-end"
      >
        <UiButton
          variant="secondary"
          color="gray"
          @click="onNewSearch"
        >
          {{ $t(`${prefix}.expired.newSearch`) }}
        </UiButton>
        <UiButton @click="onRefresh">
          {{ $t(`${prefix}.expired.refresh`) }}
        </UiButton>
      </div>
    </template>
  </UiCustomInfoDialog>
</template>
